import { Component, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Bot, Loader2 } from "lucide-react";
import { API, avatarSrc } from "@/lib/api";

async function streamChat(slug, messages, onDelta, extra = {}) {
  const res = await fetch(`${API}/public/campaigns/${slug}/chat`, {
    method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ messages, ...extra }),
  });
  if (!res.ok) throw new Error("The assistant is unavailable right now.");
  const reader = res.body.getReader();
  const dec = new TextDecoder();
  let buf = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buf += dec.decode(value, { stream: true });
    const parts = buf.split("\n\n");
    buf = parts.pop();
    for (const p of parts) {
      const data = p.replace(/^data: /, "");
      if (data === "[DONE]") return;
      let j;
      try { j = JSON.parse(data); } catch { continue; }
      if (j.error) throw new Error(j.error);
      if (j.delta) onDelta(j.delta);
    }
  }
}

const RichText = ({ text }) => String(text || "").split(/(\*\*[^*]+\*\*)/g).map((p, i) =>
  p.startsWith("**") && p.endsWith("**") ? <strong key={i}>{p.slice(2, -2)}</strong> : p.replace(/^#+\s/gm, ""));

const Avatar = ({ chat, size = "w-8 h-8" }) =>
  chat.avatar_path ? <img src={avatarSrc(chat.avatar_path)} alt="" className={`${size} rounded-full object-cover shrink-0`} />
    : <span className={`${size} rounded-full grid place-items-center shrink-0 text-white`} style={{ background: chat.color }}><Bot className="w-4 h-4" /></span>;

const loadChat = (key) => {
  try { return JSON.parse(sessionStorage.getItem(key)) || {}; } catch { return {}; }
};

const ChatWidgetInner = ({ slug, chat, preview = false, leadId = "", assetId = "" }) => {
  const storeKey = `sv_chat_${slug}`;
  const saved = useRef(preview ? {} : loadChat(storeKey)).current;
  const [open, setOpen] = useState(!!saved.open);
  const [msgs, setMsgs] = useState(Array.isArray(saved.msgs) ? saved.msgs.filter((m) => m?.content) : []);

  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!preview) sessionStorage.setItem(storeKey, JSON.stringify({ open, msgs: (busy ? msgs.slice(0, -1) : msgs).slice(-30) }));
  }, [open, msgs, busy, preview, storeKey]);
  const endRef = useRef(null);
  const greetKey = `sv_greet_${slug}`;
  const [greet, setGreet] = useState(false);
  useEffect(() => endRef.current?.scrollIntoView({ behavior: "smooth" }), [msgs]);
  useEffect(() => {
    if (!chat?.enabled || !chat.greeting || sessionStorage.getItem(greetKey)) return;
    const t = setTimeout(() => setGreet(true), 3000);
    return () => clearTimeout(t);
  }, [chat?.enabled, chat?.greeting, greetKey]);
  const dismissGreet = () => { setGreet(false); sessionStorage.setItem(greetKey, "1"); };
  const toggle = () => { dismissGreet(); setOpen((o) => !o); };

  if (!chat?.enabled) return null;
  const color = chat.color;

  const send = async (q) => {
    const content = (q ?? text).trim();
    if (!content || busy) return;
    setText("");
    const history = [...msgs, { role: "user", content }];
    setMsgs([...history, { role: "assistant", content: "" }]);
    if (preview) {
      setMsgs([...history, { role: "assistant", content: "Preview mode — open the live landing page to chat with the agent." }]);
      return;
    }
    setBusy(true);
    try {
      await streamChat(slug, history.slice(-20), (d) =>
        setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: m[m.length - 1].content + d }]), { lead_id: leadId, asset_id: assetId });
    } catch (e) {
      setMsgs((m) => [...m.slice(0, -1), { role: "assistant", content: e.message, error: true }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed bottom-5 right-5 z-50 flex flex-col items-end gap-3" style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {open && (
        <div data-testid="chat-panel" className="w-[min(380px,calc(100vw-2.5rem))] h-[min(560px,calc(100vh-7rem))] rounded-2xl bg-white text-slate-900 shadow-2xl shadow-black/40 flex flex-col overflow-hidden sv-rise">
          <div className="px-4 py-3.5 flex items-center gap-3 text-white" style={{ background: color }}>
            <Avatar chat={chat} size="w-9 h-9" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm truncate" data-testid="chat-agent-name">{chat.name}</div>
              <div className="text-[11px] opacity-80 flex items-center gap-1.5"><span className="w-1.5 h-1.5 rounded-full bg-emerald-300" /> AI assistant · online</div>
            </div>
            <button data-testid="chat-close-button" onClick={() => setOpen(false)} className="w-8 h-8 grid place-items-center rounded-full hover:bg-white/20"><X className="w-4 h-4" /></button>
          </div>
          <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-slate-50" data-testid="chat-messages">
            <div className="flex gap-2 items-end">
              <Avatar chat={chat} size="w-7 h-7" />
              <div className="max-w-[80%] rounded-2xl rounded-bl-sm bg-white border border-slate-200 px-3.5 py-2.5 text-sm" data-testid="chat-welcome">{chat.welcome}</div>
            </div>
            {msgs.length === 0 && chat.suggestions?.length > 0 && (
              <div className="flex flex-wrap gap-2 pl-9" data-testid="chat-suggestions">
                {chat.suggestions.map((s, i) => (
                  <button key={i} data-testid={`chat-suggestion-${i}`} onClick={() => send(s)}
                    className="text-xs px-3 py-1.5 rounded-full border bg-white hover:text-white transition-colors"
                    style={{ borderColor: color, color }} onMouseEnter={(e) => { e.currentTarget.style.background = color; e.currentTarget.style.color = "#fff"; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = color; }}>{s}</button>
                ))}
              </div>
            )}
            {msgs.map((m, i) => m.role === "user" ? (
              <div key={i} className="flex justify-end">
                <div className="max-w-[80%] rounded-2xl rounded-br-sm px-3.5 py-2.5 text-sm text-white whitespace-pre-wrap" style={{ background: color }} data-testid="chat-user-message">{m.content}</div>
              </div>
            ) : (
              <div key={i} className="flex gap-2 items-end">
                <Avatar chat={chat} size="w-7 h-7" />
                <div data-testid="chat-assistant-message" className={`max-w-[80%] rounded-2xl rounded-bl-sm border px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.error ? "bg-rose-50 border-rose-200 text-rose-700" : "bg-white border-slate-200"}`}>
                  {m.content ? <RichText text={m.content} /> : <span className="inline-flex gap-1 py-1"><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce" /><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:.15s]" /><span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-bounce [animation-delay:.3s]" /></span>}
                </div>
              </div>
            ))}
            <div ref={endRef} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); send(); }} className="p-3 border-t border-slate-200 bg-white flex gap-2">
            <input data-testid="chat-input" value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} placeholder="Ask a question…"
              className="flex-1 h-10 px-3.5 rounded-full bg-slate-100 text-sm focus:outline-none focus:ring-2" style={{ "--tw-ring-color": `${color}66` }} />
            <button data-testid="chat-send-button" disabled={busy || !text.trim()} className="w-10 h-10 rounded-full grid place-items-center text-white disabled:opacity-50" style={{ background: color }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
      <div className="flex items-end gap-3">
        {greet && !open && (
          <div data-testid="chat-greeting-bubble" className="relative mb-2 max-w-[240px] rounded-2xl rounded-br-sm bg-white text-slate-800 text-sm shadow-xl shadow-black/25 pl-4 pr-8 py-3 sv-rise cursor-pointer" onClick={toggle}>
            <button data-testid="chat-greeting-close" onClick={(e) => { e.stopPropagation(); dismissGreet(); }} aria-label="Close greeting"
              className="absolute top-1.5 right-1.5 w-6 h-6 grid place-items-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"><X className="w-3.5 h-3.5" /></button>
            <p className="font-semibold text-xs mb-0.5" style={{ color }}>{chat.name}</p>
            <p className="leading-snug">{chat.greeting}</p>
          </div>
        )}
        <button data-testid="chat-bubble-button" onClick={toggle} aria-label="Open chat"
          className="relative w-16 h-16 rounded-full grid place-items-center text-white shadow-xl hover:scale-105 transition-transform" style={{ background: color, boxShadow: `0 10px 30px -6px ${color}AA` }}>
          {open ? <X className="w-6 h-6" /> : chat.avatar_path
            ? <img src={avatarSrc(chat.avatar_path)} alt={chat.name} data-testid="chat-launcher-avatar" className="w-full h-full rounded-full object-cover border-[3px]" style={{ borderColor: color }} />
            : <MessageCircle className="w-6 h-6" />}
          {!open && <span data-testid="chat-online-dot" className="absolute bottom-0.5 right-0.5 w-4 h-4 rounded-full bg-emerald-500 border-2 border-white" />}
        </button>
      </div>
    </div>
  );
};

class ChatBoundary extends Component {
  state = { failed: false, n: 0 };
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(e) {
    console.error("Chat widget error", e);
    sessionStorage.removeItem(`sv_chat_${this.props.slug}`);
    if (this.state.n < 3) this.setState((s) => ({ failed: false, n: s.n + 1 }));
  }
  render() { return this.state.failed ? null : <ChatWidgetInner key={this.state.n} {...this.props} />; }
}

export const ChatWidget = (props) => <ChatBoundary {...props} />;
